import type {
  HomeAlert,
  HomeRecentContract,
  HomeUpcomingDate,
  PendingTask,
  PendingTaskKind,
} from "@closer/shared";
import {
  CONTRACT_STATUS_NAMES,
  groupPendingTasks,
  HOME_SECTION_TITLES,
  PENDING_TASK_GROUP_NAMES,
  PENDING_TASK_KIND_NAMES,
} from "@closer/shared";
import {
  AlertRow,
  Badge,
  Card,
  DeadlineRow,
  EmptyState,
  KpiStrip,
  ListRow,
  PageHeader,
  SectionCard,
  sectionCardLinkClasses,
  Skeleton,
  Table,
} from "@closer/ui";
import { useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router";

import {
  AlertIcon,
  CalendarIcon,
  ChevronRightIcon,
  ContractIcon,
  ShieldIcon,
  TaskIcon,
} from "../../components/icons";
import { StateView } from "../../components/StateView";
import { NewContractAction } from "../../layouts/NewContractAction";
import { api } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { contractPath, pendingTaskPath } from "../../lib/contractRoutes";
import { contractStatusTone } from "../../lib/contractStatusTone";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";
import { AiBriefingSection } from "./AiBriefingSection";

/**
 * ANA SAYFA (CLOS-180 `[162]`).
 *
 * ### ⚠ Dört bölüm KANONİK SIRADA ve BAĞIMSIZ
 *
 * Bekleyen İşlemler, Yaklaşan Kritik Tarihler, Son Çalışılan Sözleşmeler, Önemli Uyarılar.
 * Her bölüm kendi sorgusuyla yükleniyor: kartın kuralı *"bir bölümün boş olması diğerlerini
 * bloke etmez."* Tek sorguda toplasaydık, bir bölümün arızası bütün sayfayı düşürürdü —
 * ve Ana Sayfa, açıldığında bir şey göstermesi gereken tek ekran.
 *
 * ### UI Tur 2 §7.1 — "Güncel Özet" sayaç şeridi ve durum çubuğu
 *
 * ⚠ TUR 1'in "özet ucu yok" gerekçesiyle atladığı sayaç şeridi burada YANLIŞ atlamaydı:
 * sayılar zaten çekilen dört sorgunun `total`/uzunluğundan türüyor, yeni uç GEREKMİYOR.
 * Aynı sorgu anahtarları TanStack Query önbelleğini paylaştığı için buradaki ek
 * `useQuery` çağrıları alt bölümlerin kendi sorgularıyla AYNI ağ isteğini kullanıyor —
 * ikinci bir istek doğmuyor, yalnız üstteki özet bu veriyi de okuyor. Bölümlerin kendi
 * `isPending`/`error` durumu hâlâ kendi bileşenlerinde: üstteki özet yalnız SAYI ve
 * TAZELİK okuyor, bölümlerin bağımsızlığını bozmuyor.
 *
 * ### ⚠ "Bugün ▾" pencere seçici ATLANDI — uçta yok
 *
 * Mockup'ta "Güncel Özet" kartının sağında bir zaman penceresi seçici var. Hiçbir uç
 * istemci tarafından seçilebilir bir pencere parametresi almıyor (`homeConfigSchema`
 * sunucu tarafı SABİT limit/pencere), bu yüzden seçici ÇİZİLMEZ (ADR-0014).
 *
 * ### Panelde KARAR verilmiyor
 *
 * Kart: *"kritik işlemler detay ekranda tamamlanır."* Satırlar aksiyon düğmesi taşımıyor,
 * yalnız doğru sözleşme ve değişmez versiyona götürüyor (`[063]`'ün kuralı) — bu yüzden
 * Bekleyen İşlemler satırları `ListRow`'un `action` (buton) değil `trailing` (salt görsel)
 * yuvasını kullanıyor: satırın TAMAMI `<Link>`, ikinci bir tıklanabilir eleman yok.
 *
 * ### ⚠ Genel görev sistemi YOK
 *
 * Kartın ilk kriteri. Bölümler yalnız kanonik workflow kaynaklarından besleniyor;
 * kullanıcının kendi kendine iş açabildiği bir "yapılacaklar" listesi eklenmiyor.
 *
 * ### ⚠ Uyarılar HUKUKİ HÜKÜM DEĞİL (CLOS-182 `[164]`)
 *
 * Uyarı satırları görülen durumu söylüyor, sonucunu değil; bölüm açıklaması da bunu
 * yazıyor. "Riskli" ya da "geçersiz" demek, yazılımın veremeyeceği bir kararı vermek olurdu.
 */

function tarih(iso: string): string {
  return new Date(iso).toLocaleDateString("tr-TR", { dateStyle: "medium" });
}

function tarihSaat(iso: string): string {
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Kalan gün sayısı ve tonu — UI Tur 2 §5 `DeadlineRow` eşiği: ≤7 gün danger, ≤30 warning,
 * aksi nötr. ⚠ Bu eşik `DeadlineRow`'un İÇİNDE değil ÇAĞIRANDA: bileşen kendi başına bir
 * "kaç gün risk sayılır" kararı UYDURMAZ (kart: uyarılar hukuki hüküm değil, CLOS-182).
 */
function kalanGun(
  gun: string,
  bugun: "today" | "upcoming",
): { etiket: string; ton: "default" | "warning" | "danger" } {
  if (bugun === "today") return { etiket: "Bugün", ton: "danger" };
  const hedef = new Date(`${gun}T00:00:00`);
  const simdi = new Date(new Date().toDateString());
  const gunSayisi = Math.round((hedef.getTime() - simdi.getTime()) / 86_400_000);
  const etiket = `${gunSayisi} gün kaldı`;
  if (gunSayisi <= 7) return { etiket, ton: "danger" };
  if (gunSayisi <= 30) return { etiket, ton: "warning" };
  return { etiket, ton: "default" };
}

/** Ortak bölüm sorgusu: üçü de aynı iskeleti kullanıyor, yalnız yol ve metinler değişiyor. */
function useBolum<T>(ad: string, yol: string) {
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: activeTenantId ? tenantKey(activeTenantId, "home", ad) : ["t", "yok", "home", ad],
    enabled: activeTenantId !== null,
    queryFn: async () => (await api<T[]>(yol, { tenantId: activeTenantId! })).data,
  });
}

/**
 * ⚠ İKİ SAYI, İKİ SORU: `total` kesilen listeyi temsil ediyor (havuz işleri dahil),
 * `directTotal` ise "benden kaç iş bekleniyor" — rozetle AYNI küme (CLOS-1203 AC-7).
 */
type PendingTaskSection = { items: PendingTask[]; total: number; directTotal: number };

function usePendingTasksQuery() {
  const { activeTenantId } = useAuth();
  return useQuery({
    queryKey: activeTenantId
      ? tenantKey(activeTenantId, "home", "pending-tasks")
      : ["t", "yok", "home", "pending-tasks"],
    enabled: activeTenantId !== null,
    queryFn: async () =>
      (
        await api<PendingTaskSection>("/api/home/pending-tasks", {
          tenantId: activeTenantId!,
        })
      ).data,
  });
}

/** Tür başına gruplama — sırası öğelerin dizideki İLK GÖRÜLDÜKLERİ sıra (sunucu sırası). */
function tureGoreGrupla(items: PendingTask[]): { kind: PendingTaskKind; items: PendingTask[] }[] {
  const map = new Map<PendingTaskKind, PendingTask[]>();
  for (const item of items) {
    const grup = map.get(item.kind);
    if (grup) grup.push(item);
    else map.set(item.kind, [item]);
  }
  return [...map.entries()].map(([kind, items]) => ({ kind, items }));
}

function BekleyenIslemler() {
  const sorgu = usePendingTasksQuery();

  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          forbidden: {
            title: "Bu çalışma alanına erişiminiz değişti",
            description: "Bekleyen işleriniz görüntülenemiyor.",
          },
          error: {
            title: "Bekleyen işler yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  const satirlar = sorgu.data?.items ?? [];
  const kalan = (sorgu.data?.total ?? 0) - satirlar.length;
  /**
   * ⚠ ÖNCE TALEP TÜRÜ, SONRA İŞ TÜRÜ (CLOS-1243 — kanon §7, §28).
   *
   * D01'in iki semantik grubu üst bar paneliyle AYNI: "Benden Beklenenler" ve
   * "Üstlenebileceğim". Yalnız iş türüne göre gruplasaydık havuz işleri kişisel işlerin
   * arasına karışır ve buradaki sayı rozetle çelişirdi — aynı görev iki yüzeyde iki farklı
   * şey söylerdi.
   */
  const { direct: benden, claimable: ustlenebilecegim } = groupPendingTasks(satirlar);
  const bloklar = (
    [
      ["direct", benden],
      ["claimable", ustlenebilecegim],
    ] as const
  )
    .filter(([, kume]) => kume.length > 0)
    .map(([grup, kume]) => ({ grup, gruplar: tureGoreGrupla(kume) }));

  return (
    <SectionCard
      title={HOME_SECTION_TITLES.pendingTasks}
      description="Sizden beklenen ve üstlenebileceğiniz inceleme, onay, iş akışı aşaması, imza ve mutabakat işleri."
      footer={
        kalan > 0 ? (
          /**
           * ⚠ `/app` ÖNEKİ ŞART (kusur avı, 26 Ağustos): tam liste kabuğun altında
           * (`/app/bekleyen-islemler`). Önek düştüğünde bağlantı hiçbir yere gitmiyordu.
           */
          <Link to="/app/bekleyen-islemler" className={sectionCardLinkClasses}>
            {kalan} iş daha var; tam listeyi aç
          </Link>
        ) : undefined
      }
    >
      {sorgu.isPending ? (
        <div role="status" aria-label={`${HOME_SECTION_TITLES.pendingTasks} yükleniyor`}>
          <Skeleton variant="row" lines={3} />
        </div>
      ) : hataDurumu ? (
        <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
      ) : bloklar.length === 0 ? (
        <EmptyState
          icon={<TaskIcon />}
          title="Bekleyen bir işiniz yok."
          description="Sizden beklenen ve üstlenebileceğiniz inceleme, onay, iş akışı aşaması, imza ve mutabakat işleri burada görünür."
        />
      ) : (
        bloklar.map(({ grup, gruplar }) => (
          <section key={grup} className="flex flex-col gap-2">
            {/*
              ⚠ Başlık yalnız İKİ blok da varsa çiziliyor: tek grup varken başlık atmak,
              olmayan bir ayrımı varmış gibi göstermek olurdu.
            */}
            {bloklar.length > 1 ? (
              <h3
                className="text-xs font-medium text-fg-soft"
                data-testid={`ana-bekleyen-grup-${grup}`}
              >
                {PENDING_TASK_GROUP_NAMES[grup]}
              </h3>
            ) : null}
            <ul className="flex flex-col gap-2">
              {gruplar.map((grup) => {
                const ilk = grup.items[0]!;
                return (
                  <li key={grup.kind}>
                    <Link to={pendingTaskPath(ilk)} className="contents">
                      <ListRow
                        icon={<TaskIcon className="size-[18px]" />}
                        title={PENDING_TASK_KIND_NAMES[grup.kind]}
                        count={<Badge>{grup.items.length}</Badge>}
                        /**
                         * ⚠ TALEP EDEN YALNIZ TEK İŞLİK GRUPTA (CLOS-1112 · R5 §12.3). Kullanıcının
                         * sorusu ("bunu benden kim istedi?") burada da soruluyor, ama bu kart
                         * TÜRE GÖRE grupluyor: dört inceleme işi dört ayrı kişiden gelmiş olabilir.
                         * Grubun ilk satırının adını yazmak, diğer üçünü de o kişiden gelmiş gibi
                         * gösterirdi — eksik bilgi değil, YANLIŞ bilgi. Grup çoksa cevap tam
                         * listede: "Bekleyen İşlemler" sayfası her satırın talep edenini yazıyor.
                         */
                        subtitle={
                          grup.items.length > 1
                            ? `${ilk.contractTitle} ve ${grup.items.length - 1} diğer sözleşme`
                            : [
                                ilk.contractTitle,
                                ilk.requestedByName ? `Talep eden: ${ilk.requestedByName}` : null,
                              ]
                                .filter(Boolean)
                                .join(" · ")
                        }
                        trailing={
                          <span className="flex items-center gap-1 text-[13px] font-medium text-accent">
                            İncele
                            <ChevronRightIcon className="size-4" />
                          </span>
                        }
                      />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </SectionCard>
  );
}

function YaklasanTarihler() {
  const sorgu = useBolum<HomeUpcomingDate>("upcoming-dates", "/api/home/upcoming-dates");
  const satirlar = sorgu.data ?? [];
  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          error: {
            title: "Kritik tarihler yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  return (
    <SectionCard
      title={HOME_SECTION_TITLES.upcomingDates}
      description="Yürürlük, bitiş, fesih bildirimi ve yenileme tarihleri."
      /**
       * ⚠ "Tümünü gör ›" ATLANDI — uçta yok. Mockup'ta bu bölümün başlığının sağında bir
       * tam-liste bağlantısı var, ama "tüm kritik tarihler" diye ayrı bir sayfa/uç
       * bulunmuyor; `/app/sozlesmeler`'e bağlamak kritik tarih listesiyle ilgisiz bir
       * yere yönlendirirdi (ADR-0014: uydurma yönlendirme yok).
       */
    >
      {sorgu.isPending ? (
        <div role="status" aria-label={`${HOME_SECTION_TITLES.upcomingDates} yükleniyor`}>
          <Skeleton variant="row" lines={3} />
        </div>
      ) : hataDurumu ? (
        <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
      ) : satirlar.length === 0 ? (
        <EmptyState icon={<CalendarIcon />} title="Yaklaşan kritik tarih yok." />
      ) : (
        <ul className="flex flex-col gap-2">
          {satirlar.map((satir) => {
            const { etiket, ton } = kalanGun(satir.day, satir.standing);
            return (
              <li key={satir.id}>
                <Link to={contractPath(satir.contractId, null)} className="contents">
                  <DeadlineRow
                    date={satir.day}
                    title={satir.contractTitle}
                    subtitle={satir.label}
                    remainingLabel={etiket}
                    tone={ton}
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

function SonCalisilanlar() {
  /**
   * MÜVEKKİL ALANI BAĞLAMI (CLOS-1128 · Faz 5 UX borcu): alan bağlamında genel sözleşme
   * listesi boştur; müvekkilin sözleşmeleri `/app/muvekkil-alani`'nda yaşar. Bağlantı oraya.
   */
  const { activeTenant, can } = useAuth();
  const sozlesmeListesiYolu =
    activeTenant?.type === "client_workspace" ? "/app/muvekkil-alani" : "/app/sozlesmeler";
  const sorgu = useBolum<HomeRecentContract>("recent-contracts", "/api/home/recent-contracts");
  const satirlar = sorgu.data ?? [];
  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          error: {
            title: "Son çalışılanlar yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  /**
   * ⚠ "Karşı Taraf" ve "⋯" sütunları ATLANDI — uçta yok. Mockup'ın tablosu bu iki sütunu
   * da gösteriyor ama `HomeRecentContract` yalnız `contractId/contractTitle/status/
   * lastTouchedAt` taşıyor (`packages/shared/src/schemas/home.ts`); karşı taraf adı bu
   * projeksiyonda YOK ve ⋯ menüsünün aksiyonları (bu özet satırında) bir davranış
   * sözleşmesi gerektirirdi. Kartsız yüzey yasağı (ADR-0014): olmayan veri/davranış
   * çizilmez.
   */
  const columns: ColumnDef<HomeRecentContract>[] = [
    {
      accessorKey: "contractTitle",
      header: "Sözleşme Adı",
      cell: ({ row }) => (
        <Link
          to={contractPath(row.original.contractId, null)}
          className="flex items-center gap-3 font-medium text-fg-strong hover:text-primary hover:underline"
        >
          <span
            aria-hidden="true"
            className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary-soft text-primary"
          >
            <ContractIcon className="size-[18px]" />
          </span>
          <span className="min-w-0 truncate">{row.original.contractTitle}</span>
        </Link>
      ),
    },
    {
      accessorKey: "status",
      header: "Durum",
      cell: ({ row }) => (
        <Badge tone={contractStatusTone(row.original.status)}>
          {CONTRACT_STATUS_NAMES[row.original.status as keyof typeof CONTRACT_STATUS_NAMES] ??
            row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: "lastTouchedAt",
      header: "Son Düzenleme",
      cell: ({ row }) => tarih(row.original.lastTouchedAt),
    },
  ];

  return (
    <SectionCard
      title={HOME_SECTION_TITLES.recentContracts}
      description="Üzerinde en son çalıştığınız kayıtlar."
      footer={
        satirlar.length > 0 ? (
          <Link to="/app/sozlesmeler" className={sectionCardLinkClasses}>
            Tüm sözleşmeleri görüntüle ›
          </Link>
        ) : undefined
      }
    >
      {sorgu.isPending ? (
        <div role="status" aria-label={`${HOME_SECTION_TITLES.recentContracts} yükleniyor`}>
          <Skeleton variant="row" lines={3} />
        </div>
      ) : hataDurumu ? (
        <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
      ) : satirlar.length === 0 ? (
        /*
          ⚠ İLK ADIM, YETKİYE GÖRE (CLOS-1279 · matris 11).

          Burada yalnız "Sözleşmelere git" vardı. Doğru ama zayıf: hiç sözleşmesi olmayan
          yeni bir organizasyonda o bağlantı kullanıcıyı BOŞ BİR LİSTEYE götürüyordu.
          Belge §4.2 ilk oturumu "İlk sözleşmeni oluştur" diye tarif ediyor.

          ⚠ Liste bağlantısı KALDIRILMADI: oluşturma yetkisi olmayan kullanıcı için tek
          anlamlı adım hâlâ o. Aksiyonu herkese gösterseydik, `ContractsPage`'te
          düzelttiğimiz "olmayan düğmeyi tarif etme" kusurunu buraya taşırdık.
        */
        <EmptyState
          icon={<ContractIcon />}
          title="Henüz bir sözleşme üzerinde çalışmadınız."
          action={
            can("contract.create") ? (
              <NewContractAction />
            ) : (
              <Link to={sozlesmeListesiYolu} className={sectionCardLinkClasses}>
                Sözleşmelere git
              </Link>
            )
          }
        />
      ) : (
        <Table columns={columns} data={satirlar} />
      )}
    </SectionCard>
  );
}

function OnemliUyarilar() {
  const sorgu = useBolum<HomeAlert>("alerts", "/api/home/alerts");
  const satirlar = sorgu.data ?? [];
  const hataDurumu = sorgu.error
    ? resolveErrorState(sorgu.error, {
        idempotent: true,
        existenceIsSecret: false,
        overrides: {
          error: {
            title: "Uyarılar yüklenemedi",
            description: "Bağlantı sorunları geçici olabilir.",
          },
        },
      })
    : null;

  return (
    <SectionCard
      title={HOME_SECTION_TITLES.alerts}
      description="Dikkat etmeniz gereken durumlar; hukuki bir değerlendirme değildir."
      /**
       * ⚠ "Tümünü gör ›" ATLANDI — uçta yok, `YaklasanTarihler` ile aynı gerekçe: "tüm
       * uyarılar" diye ayrı bir sayfa/uç bulunmuyor, uydurma bir hedefe bağlamak
       * ADR-0014'ü ihlal ederdi.
       */
    >
      {sorgu.isPending ? (
        <div role="status" aria-label={`${HOME_SECTION_TITLES.alerts} yükleniyor`}>
          <Skeleton variant="row" lines={3} />
        </div>
      ) : hataDurumu ? (
        <StateView state={hataDurumu} onRetry={() => void sorgu.refetch()} />
      ) : satirlar.length === 0 ? (
        <EmptyState icon={<ShieldIcon />} title="Şu an önemli bir uyarı yok." />
      ) : (
        <ul className="flex flex-col gap-2">
          {satirlar.map((uyari) =>
            /* ⚠ Çalışma alanı düzeyindeki uyarı bir KAYDA yönlendirmiyor (contractId=null). */
            uyari.contractId ? (
              <li key={uyari.id}>
                <Link to={contractPath(uyari.contractId, null)} className="contents">
                  <AlertRow
                    icon={<AlertIcon className="size-[18px]" />}
                    title={uyari.message}
                    description={uyari.contractTitle ?? ""}
                    count={<ChevronRightIcon className="size-4 text-fg-faint" />}
                  />
                </Link>
              </li>
            ) : (
              <li key={uyari.id}>
                <AlertRow
                  icon={<AlertIcon className="size-[18px]" />}
                  title={uyari.message}
                  description="Çalışma alanı düzeyinde bir uyarı."
                  count=""
                />
              </li>
            ),
          )}
        </ul>
      )}
    </SectionCard>
  );
}

export function HomePage() {
  /**
   * ⚠ Bu üç sorgu ALT BÖLÜMLERİN sorgularıyla AYNI anahtarı kullanıyor — TanStack Query
   * önbelleği paylaşır, ikinci bir ağ isteği doğmaz. Yalnız "Güncel Özet" sayaç şeridi
   * ve durum çubuğu için SAYI/TAZELİK okunuyor; bölümlerin kendi yükleniyor/hata/boş
   * durumları hâlâ aşağıdaki bağımsız bileşenlerde.
   */
  const bekleyenIslemler = usePendingTasksQuery();
  const yaklasanTarihler = useBolum<HomeUpcomingDate>("upcoming-dates", "/api/home/upcoming-dates");
  const uyarilar = useBolum<HomeAlert>("alerts", "/api/home/alerts");
  const sonCalisilanlar = useBolum<HomeRecentContract>(
    "recent-contracts",
    "/api/home/recent-contracts",
  );

  const enSonGuncelleme = Math.max(
    bekleyenIslemler.dataUpdatedAt,
    yaklasanTarihler.dataUpdatedAt,
    uyarilar.dataUpdatedAt,
    sonCalisilanlar.dataUpdatedAt,
  );

  return (
    <div className="flex w-full flex-col gap-6">
      <PageHeader
        title="Ana Sayfa"
        description="Bugün dikkat gerektiren sözleşme işlerinizi buradan takip edin."
      />

      {/*
        GÜNLÜK AI ÖZETİ (CLOS-290 `[194]`) — dört bölümün ÜSTÜNDE ve onlardan AYRI bir
        kart: özet o bölümlerin bir okuması, yerine geçen bir kaynak değil. Kapalı
        kurulumda bölüm hiç çizilmiyor.
      */}
      <AiBriefingSection />

      {/*
        ⚠ `Card` DEĞİL, düz şerit — ölçülerek bulundu. Card children'ı her zaman kendi
        `p-5`/`p-6` gövde `div`'ine sarıyor; dışarıdan verilen `flex` yalnız dıştaki
        `section`'a iniyor ve içerideki span'ler block akışında kalıyordu. Sonuç ekranda
        görünüyordu: yeşil nokta ve ayraç çizgisi inline span oldukları için `size-2` /
        `w-px` almıyor (ölçüldü: genişlik 0), iki metin de aralarında hiç boşluk olmadan
        "Veriler güncelSon güncelleme: …" diye bitişik çiziliyordu.

        Şerit zaten bir kart değil — kartın kenarlık/zemin dili korunuyor, taşıyıcısı
        kendi flex kabı oluyor.
      */}
      {enSonGuncelleme > 0 ? (
        <div className="flex h-11 items-center gap-2.5 rounded-lg border border-border-default bg-surface px-5">
          <span aria-hidden="true" className="size-2 rounded-full bg-success" />
          <span className="text-[13px] font-medium text-fg-strong">Veriler güncel</span>
          <span aria-hidden="true" className="h-3.5 w-px bg-border-default" />
          <span className="text-[13px] text-fg-muted">
            Son güncelleme: {tarihSaat(new Date(enSonGuncelleme).toISOString())}
          </span>
        </div>
      ) : null}

      {/**
       * ⚠ Responsive: dar ekranda tek sütun, geniş ekranda 2:1. Onaylı tasarımda solda
       * geniş sütun (bekleyen işler, son çalışılanlar), sağda dar sütun (Güncel Özet +
       * tarihler, uyarılar).
       *
       * ⚠ DÖRT BÖLÜM AYRI ÜST-SEVİYE GRID ÖĞESİ — sarmalayıcı sütun `div`'leri İLE
       * KURULMUYOR. Bölümleri sütun sütun sarsaydık DOM sırası "bekleyen, son çalışılan,
       * tarihler, uyarılar" olurdu; kartın saydığı kanonik sıra ise "bekleyen, tarihler,
       * son çalışılan, uyarılar" ve bir test bunu `region` etiketlerinden okuyor. "Güncel
       * Özet" kartı bir `region` DEĞİL (dört bölüme dahil değil); yalnız DOM'da Yaklaşan
       * Kritik Tarihler'in ÖNÜNDE durduğu için kanonik region sırası bozulmuyor.
       */}
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2 xl:col-start-1 xl:row-start-1">
          <BekleyenIslemler />
        </div>
        <Card title="Güncel Özet" className="xl:col-start-3 xl:row-start-1">
          <KpiStrip
            items={[
              {
                key: "pendingTasks",
                icon: <TaskIcon />,
                /**
                 * ⚠ `directTotal`, `total` DEĞİL (CLOS-1203 AC-7).
                 *
                 * `total` listenin altındaki "daha fazlası var" bilgisi ve havuz işlerini
                 * de sayıyor. Sayaç burada onu gösterirken üst bar rozeti yalnız
                 * "Benden Beklenenler"i sayıyordu: aynı ekranda iki farklı gerçek. Üç
                 * kişilik bir `any` havuzunda rozet "0" derken özet "1" diyordu.
                 */
                value: bekleyenIslemler.data?.directTotal ?? 0,
                label: "Bekleyen İşlemler",
              },
              {
                key: "upcomingDates",
                icon: <CalendarIcon />,
                value: yaklasanTarihler.data?.length ?? 0,
                label: "Kritik Tarihler",
              },
              {
                key: "alerts",
                icon: <AlertIcon />,
                value: uyarilar.data?.length ?? 0,
                label: "Uyarılar",
              },
            ]}
          />
        </Card>
        <div className="xl:col-start-3 xl:row-start-2">
          <YaklasanTarihler />
        </div>
        <div className="xl:col-span-2 xl:col-start-1 xl:row-start-2">
          <SonCalisilanlar />
        </div>
        <div className="xl:col-start-3 xl:row-start-3">
          <OnemliUyarilar />
        </div>
      </div>
    </div>
  );
}
